/**
 * AI Note Preview — generic, note-type agnostic renderer
 *
 * The socket sends `{ noteType, sections }` where `sections` is a flat map of
 * `key -> text`. Nothing here knows a key name: every key becomes a section, its
 * label is derived from the key, and its value is the body.
 *
 * The two legacy note types (SIMPLE_SOAP_NOTE / PRIMARY_CARE_SOAP_NOTE) keep the
 * view that was already built for them; `ScribeDraftPanel` picks between the two
 * with `isLegacyNoteType`, so this component only ever renders the generic view.
 *
 * Both views render through `ScribeNoteCard`, so the note surface — document
 * card, spacing and section styling — is identical whatever the note type.
 */

import { SxProps, Theme } from "@mui/material";
import { useMemo } from "react";
import { FlexCenterWrapper } from "@/components/DefaultNoDataWrapper";
import Text from "@/components/Text";
import ScribeNoteCard from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/ai-scriber/v2/components/ScribeNoteCard";
import { SCRIBE_V2_COLORS } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/ai-scriber/v2/constants";

/** Note types that must keep the previously implemented SOAP view. */
export const LEGACY_NOTE_TYPES = [
  "SIMPLE_SOAP_NOTE",
  "PRIMARY_CARE_SOAP_NOTE",
] as const;

export const NOTE_PREVIEW_LABELS = {
  EMPTY_SECTION: "-",
  NO_NOTE: "No note was generated for this session",
} as const;

/** Anything the socket echoes back that is not a note section. */
const NON_SECTION_KEYS = [
  "patientId",
  "appointmentId",
  "transcriptionSegments",
  "noteType",
];

export type LegacyNoteType = (typeof LEGACY_NOTE_TYPES)[number];

/** `data` of the socket envelope. `sections` keys are unknown by design. */
export interface AiNotePayload {
  noteType?: string;
  sections?: Record<string, unknown>;
  [key: string]: unknown;
}

/** One flattened, renderable section. */
export interface AiNoteSection {
  key: string;
  label: string;
  content: string;
}

export interface AiNotePreviewProps {
  /** The socket's `data` object. */
  data?: AiNotePayload;
  /** Heading on the note card — the same one the SOAP view uses. */
  title: string;
  /** The legacy SOAP view is chosen by the caller, not passed in here. */
  // legacyView?: ReactNode;
  /** Hides sections the generator returned empty instead of showing "-". */
  hideEmptySections?: boolean;
  sx?: SxProps<Theme>;
}

/** True for the note types that keep the previously implemented view. */
export const isLegacyNoteType = (
  noteType?: string
): noteType is LegacyNoteType =>
  LEGACY_NOTE_TYPES.includes(noteType as LegacyNoteType);

/** `followUpInstructions` / `FOLLOW_UP` → `Follow Up Instructions`. */
export const humanizeSectionKey = (key: string): string =>
  key
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/(?:^|\s)(\w)/g, (match) => match.toUpperCase());

/** Values arrive as strings; nested maps/arrays are flattened to one line. */
const toContent = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (Array.isArray(value))
    return value.map(toContent).filter(Boolean).join(", ");
  if (value && typeof value === "object")
    return Object.entries(value as Record<string, unknown>)
      .map(([key, nested]) => {
        const content = toContent(nested);
        return content ? `${humanizeSectionKey(key)}: ${content}` : "";
      })
      .filter(Boolean)
      .join(" · ");
  return "";
};

/**
 * Flattens `sections` into an ordered list of `{ key, label, content }`, keeping
 * the order the backend sent. Works for any note type, current or future.
 */
// Some templates (Psychiatric Note) send their sections flat on `data`, with no `sections` wrapper.
const getSectionMap = (data?: AiNotePayload): Record<string, unknown> =>
  data?.sections ?? data ?? {};

export const toAiNoteSections = (data?: AiNotePayload): AiNoteSection[] =>
  Object.entries(getSectionMap(data))
    .filter(([key]) => !NON_SECTION_KEYS.includes(key))
    .map(([key, value]) => ({
      key,
      label: humanizeSectionKey(key),
      content: toContent(value),
    }));

const AiNotePreview = ({
  data,
  title,
  hideEmptySections = false,
  sx,
}: AiNotePreviewProps) => {
  const sections = useMemo(() => toAiNoteSections(data), [data]);

  const visibleSections = hideEmptySections
    ? sections.filter((section) => section.content)
    : sections;

  if (visibleSections.length === 0)
    return (
      <FlexCenterWrapper>
        <Text sx={{ color: SCRIBE_V2_COLORS.textSecondary }}>
          {NOTE_PREVIEW_LABELS.NO_NOTE}
        </Text>
      </FlexCenterWrapper>
    );

  return (
    <ScribeNoteCard
      title={title}
      sections={visibleSections.map(({ key, label, content }) => ({
        key,
        label,
        paragraphs: [{ text: content || NOTE_PREVIEW_LABELS.EMPTY_SECTION }],
      }))}
      sx={sx}
    />
  );
};

export default AiNotePreview;
